import React, { useEffect } from 'react';
import { Form } from './Form';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { NavigationType, OperationType } from '../types/enums';
import { useLocation, useParams } from 'react-router-dom';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { useAppDispatch, useTypedSelector } from '../../../combineReducers';
import { getSurveyConfig } from '../helpers/common';

const useStyles = makeStyles(() =>
  createStyles({
    mainContainer: {
      width: '100%',
      maxHeight: '100%',
      overflow: 'auto',
      display: 'flex'
    }
  })
);

const operationSessionKeys = {
  selectedProducts: 'survey:orderPageSelectedProduct_',
  form: 'survey:formState_',
  isNavigateFromCheckout: 'survey:checkout_',
  isUnmountSkipped: 'survey:isUnmountSkipped_',
  isWindowSizeChanged: 'survey:isWindowSizeChanged_'
};

type Props = {
  operationType: OperationType;
};

export const SurveyPage: React.FC<Props> = ({ operationType }) => {
  const classes = useStyles();
  const { organizationId } = useParams<{ organizationId: string }>();
  const currentLocation = useLocation();
  const dispatch = useAppDispatch();
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const isNavigateFromCheckoutKey = `${operationSessionKeys.isNavigateFromCheckout}${organizationId}_${operationType}`;
  const isUnmountSkippedKey = `${operationSessionKeys.isUnmountSkipped}${organizationId}_${operationType}`;
  const isWindowSizeChangedKey = `${operationSessionKeys.isWindowSizeChanged}${organizationId}_${operationType}`;

  const clearOperationStoredData = () => {
    Object.entries(operationSessionKeys).forEach(([_, value]) => {
      sessionStorage.removeItem(`${value}${organizationId}_${operationType}`);
    });
  };

  React.useEffect(() => {
    organizationId && !mainSubscription.id && dispatch(fetchMainSubscription(organizationId));
  }, [organizationId, dispatch, mainSubscription]);

  useEffect(() => {
    const navigationType = (window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type;
    const isPageReload = navigationType === NavigationType.Reload;

    if (!!sessionStorage.getItem(isNavigateFromCheckoutKey)) {
      sessionStorage.removeItem(isNavigateFromCheckoutKey);
    } else if (
      !!sessionStorage.getItem(isUnmountSkippedKey) &&
      !sessionStorage.getItem(isWindowSizeChangedKey) &&
      !isPageReload
    ) {
      clearOperationStoredData();
    }

    sessionStorage.setItem(isUnmountSkippedKey, 'true');
    sessionStorage.removeItem(isWindowSizeChangedKey);

    return () => {
      if (!window.location.pathname.toLowerCase().includes(currentLocation.pathname.toLowerCase())) {
        clearOperationStoredData();
      } else {
        sessionStorage.setItem(isWindowSizeChangedKey, 'true');
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.mainContainer}>
      <Form surveyConfig={getSurveyConfig(operationType, mainSubscription)} />
    </div>
  );
};
